/**
 * AsValueList Web Component
 *
 * Renders an ordered, editable list of primitive values. Supports float, int,
 * string, and bool element types. When linked to a Paramate array parameter via
 * trcad-param the element type is inferred automatically from the param data.
 *
 * Attributes:
 *   label-default-text   — optional label shown above the list
 *   element-type         — 'float' | 'int' | 'string' | 'bool'
 *                          Required when used without trcad-param.
 *                          Inferred from param metadata when trcad-param is set.
 *   value                — JSON array string for initial value without Paramate,
 *                          e.g. value="[0,1,2]"
 *   trcad-param          — Paramate parameter name (must be of type "array")
 *   configure-on-change  — trigger configure() immediately on every change
 *   debounce-ms          — Paramate send debounce in ms (default 150)
 *   no-debounce          — disables debounce
 *
 * Events dispatched:
 *   as-input-change — a committed change that should drive app logic: a user
 *     edit, or an explicit setValue()
 *     detail: { type: 'array', value, previousValue, elementType, reason, phase }
 *   as-input-sync — the same, for a change that should not drive app logic: a
 *     project load, an undo, a param sync, a reset, or a quiet `.value =`
 *
 * Built on AsInputBase, so it also gets trcad-param registration, debounce,
 * cooloff, readonly/locked, undo history, bindWithAppData, default-value and
 * resetToDefault — none of which its hand-rolled predecessor had.
 */

import './numbox.ts';
import './as-string-input.ts';
import './as-checkbox.ts';
import { AsInputBase, type InternalSetValueOptions } from './as-input-base.ts';
import type { InputChangeReason, SetValueOptions } from './types.ts';
import { resolveSetValueSilence } from './utils.ts';
import styleText from './as-value-list.css?raw';

type ElementType = 'float' | 'int' | 'string' | 'bool';

function defaultForType(type: ElementType): any {
  if (type === 'string') return '';
  if (type === 'bool') return false;
  return 0;
}

export class AsValueList extends AsInputBase<'vector'> {
  static readonly tag = 'as-value-list' as const;
  private static _lightStyleAppended = false;

  private _elementType: ElementType | null = null;
  private _items: any[] = [];
  private _hasRendered = false;
  private _listEl: HTMLElement | null = null;
  private _placement: string | null = null;
  // Index of a newly-added row that is pending user confirmation (Enter/Escape)
  private _pendingRowIndex: number | null = null;

  static get observedAttributes() {
    return ['label-default-text', 'element-type', 'placement'];
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  connectedCallback() {
    super.connectedCallback();
    if (!this._hasRendered) {
      AsValueList._appendStyles();
      this._render();
      this._hasRendered = true;
    }

    const elemTypeAttr = this.getAttribute('element-type') as ElementType | null;
    if (elemTypeAttr) this._elementType = elemTypeAttr;

    this._placement = this.getAttribute('placement');

    // Parse the value attribute as a JSON array for standalone use.
    const valueAttr = this.getAttribute('value');
    if (valueAttr && this._items.length === 0) {
      this._items = this._convertFromParamate(valueAttr);
    }

    // Everything else — the param registry, the configurator handshake, the
    // debounce/cooloff attributes, restore, reset and history — comes from
    // AsInputBase. This used to be ~250 lines of hand-copied duplicates that
    // had already drifted (no as-input-update, no history, no readonly).
    this._registerParamate();

    this._renderList();
  }

  disconnectedCallback() {
    this._unregisterParamate();
    super.disconnectedCallback();
  }

  attributeChangedCallback(name: string, _old: string | null, newValue: string | null) {
    switch (name) {
      case 'label-default-text': {
        const el = this.querySelector('.as-value-list__label-text');
        if (el) el.textContent = newValue ?? '';
        break;
      }
      case 'element-type':
        this._elementType = newValue as ElementType | null;
        this._renderList();
        break;
      case 'placement':
        this._placement = newValue;
        this._renderList();
        break;
    }
  }

  // ============================================================================
  // Styles
  // ============================================================================

  private static _appendStyles() {
    if (AsValueList._lightStyleAppended) return;
    const styleEl = document.createElement('style');
    styleEl.textContent = styleText;
    styleEl.setAttribute('data-component', 'as-value-list');
    document.head.appendChild(styleEl);
    AsValueList._lightStyleAppended = true;
  }

  // ============================================================================
  // Rendering
  // ============================================================================

  private _render() {
    this.innerHTML = `
      <div class="as-value-list">
        <div class="as-value-list__header">
          <span class="as-value-list__label-text">${this.getAttribute('label-default-text') ?? ''}</span>
        </div>
        <div class="as-value-list__items"></div>
        <button class="as-value-list__add-btn" type="button">+ Add item</button>
      </div>
    `;

    this._listEl = this.querySelector('.as-value-list__items');

    // Stop child input events from leaking into the global event stream.
    // We re-dispatch our own as-input-change with the full array instead.
    this._listEl?.addEventListener('as-input-update', (e: Event) => {
      e.stopPropagation();
    });

    this._listEl?.addEventListener('as-input-change', (e: Event) => {
      e.stopPropagation();
      const row = (e.target as HTMLElement).closest('[data-vl-index]') as HTMLElement | null;
      if (!row) return;
      const index = parseInt(row.dataset.vlIndex ?? '', 10);
      if (isNaN(index)) return;
      // Ignore changes from the pending (unconfirmed) row — wait for Enter
      if (index === this._pendingRowIndex) return;
      const value = AsValueList._readInputValue(e.target as HTMLElement);
      if (value !== undefined) {
        const previous = [...this._items];
        this._items[index] = value;
        this._emitChange(previous, 'change', 'user');
      }
    });

    this._listEl?.addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest(
        '.as-value-list__remove-btn',
      ) as HTMLElement | null;
      if (!btn) return;
      const row = btn.closest('[data-vl-index]') as HTMLElement | null;
      if (!row) return;
      const index = parseInt(row.dataset.vlIndex ?? '', 10);
      if (isNaN(index)) return;
      this._removeItem(index);
    });

    this.querySelector('.as-value-list__add-btn')?.addEventListener('click', () => {
      this._addItem();
    });
  }

  private _renderList() {
    if (!this._listEl) return;
    // A full re-render destroys any pending row, so clear the flag
    this._pendingRowIndex = null;
    this._listEl.innerHTML = '';
    this._items.forEach((val, i) => {
      this._listEl!.appendChild(this._createRow(i, val));
    });
  }

  private _createRow(index: number, value: any): HTMLElement {
    const row = document.createElement('div');
    row.className = 'as-value-list__row';
    row.dataset.vlIndex = String(index);

    row.appendChild(this._createInput(value));

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'as-value-list__remove-btn';
    removeBtn.textContent = '−';
    removeBtn.title = 'Remove';
    row.appendChild(removeBtn);

    return row;
  }

  private _createInput(value: any): HTMLElement {
    const type = this._elementType ?? 'float';
    let el: HTMLElement;

    switch (type) {
      case 'int': {
        el = document.createElement('as-numbox');
        el.setAttribute('value', String(value ?? 0));
        el.setAttribute('type', 'int');
        el.setAttribute('no-debounce', '');
        if (this._placement) el.setAttribute('placement', this._placement);
        break;
      }
      case 'string': {
        el = document.createElement('as-string-input');
        el.setAttribute('value', String(value ?? ''));
        el.setAttribute('no-debounce', '');
        break;
      }
      case 'bool': {
        el = document.createElement('as-checkbox');
        if (value) el.setAttribute('checked', '');
        break;
      }
      case 'float':
      default: {
        el = document.createElement('as-numbox');
        el.setAttribute('value', String(value ?? 0));
        el.setAttribute('no-debounce', '');
        if (this._placement) el.setAttribute('placement', this._placement);
        break;
      }
    }

    return el;
  }

  private static _readInputValue(el: HTMLElement): any {
    switch (el.tagName.toLowerCase()) {
      case 'as-numbox':
      case 'as-string-input':
        return (el as any).value;
      case 'as-checkbox':
        return (el as any).checked;
      default:
        return undefined;
    }
  }

  // ============================================================================
  // Item manipulation
  // ============================================================================

  private _addItem() {
    // Confirm any in-flight pending item before opening a new one
    if (this._pendingRowIndex !== null) {
      this._confirmPendingItem(this._pendingRowIndex);
    }

    const defaultVal = this._elementType ? defaultForType(this._elementType) : 0;
    this._items = [...this._items, defaultVal];
    const index = this._items.length - 1;
    this._pendingRowIndex = index;

    const row = this._createRow(index, defaultVal);
    row.classList.add('as-value-list__row--pending');
    this._listEl?.appendChild(row);

    // Intercept keydown in capture phase so we read the raw <input> value
    // before any child component's own Enter/Escape handler runs.
    row.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this._pendingRowIndex !== index) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        this._confirmPendingItem(index, row);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelPendingItem(index);
      }
    }, { capture: true });

    // Focus and select the inner <input> after the child component has rendered
    requestAnimationFrame(() => {
      const inner = row.querySelector('input') as HTMLInputElement | null;
      if (inner) {
        inner.focus();
        inner.select?.();
      }
    });

    // Do NOT emit or send to Paramate — wait for confirmation
  }

  /**
   * Commit the pending row: read the current raw <input> value (which the child
   * component may not have committed yet), update _items, then emit and send.
   */
  private _confirmPendingItem(index: number, rowEl?: HTMLElement) {
    const previous = [...this._items];
    const row = rowEl
      ?? (this._listEl?.querySelector(`[data-vl-index="${index}"]`) as HTMLElement | null);

    if (row) {
      const inner = row.querySelector('input') as HTMLInputElement | null;
      if (inner) {
        const type = this._elementType ?? 'float';
        if (type === 'float') {
          const p = parseFloat(inner.value);
          this._items[index] = isNaN(p) ? 0 : p;
        } else if (type === 'int') {
          const p = parseFloat(inner.value);
          this._items[index] = isNaN(p) ? 0 : Math.round(p);
        } else if (type === 'string') {
          this._items[index] = inner.value;
        } else if (type === 'bool') {
          this._items[index] = inner.checked;
        }
      }
      row.classList.remove('as-value-list__row--pending');
    }

    this._pendingRowIndex = null;
    this._emitChange(previous, 'change', 'user');
  }

  /** Remove the pending (unconfirmed) row without sending to Paramate. */
  private _cancelPendingItem(index: number) {
    this._items.splice(index, 1);
    this._pendingRowIndex = null;
    this._renderList();
  }

  private _removeItem(index: number) {
    // If the user removes the pending row treat it as a cancel — no emit/send
    if (index === this._pendingRowIndex) {
      this._cancelPendingItem(index);
      return;
    }
    const previous = [...this._items];
    this._items = this._items.filter((_, i) => i !== index);
    this._renderList();
    this._emitChange(previous, 'change', 'user');
  }

  // ============================================================================
  // Events
  // ============================================================================

  private _emitChange(
    previousValue: any[] = this._items,
    phase: 'change' | 'sync' = 'change',
    reason: InputChangeReason = 'user',
  ) {
    this._emitValueEvent(phase, reason, {
      type: 'array',
      value: [...this._items],
      previousValue: [...previousValue],
      elementType: this._elementType,
    });

    // Ground truth coming back from the configurator must not be sent straight
    // back to it, and a sync-phase change isn't a user edit to push either.
    if (phase !== 'change' || reason === 'paramate') return;
    this._sendToParamate(this._getValue());
  }

  // ============================================================================
  // Paramate integration
  // ============================================================================

  // ============================================================================
  // AsInputBase abstract method implementations
  // ============================================================================

  protected _getValue(): any[] {
    return [...this._items];
  }

  protected _applyValue(val: any[], options: InternalSetValueOptions): void {
    if (!Array.isArray(val)) return;
    const previous = this._items;
    if (this._areValuesEqual(val, previous)) return;

    this._items = [...val];
    this._renderList();

    if (options.silent) return;
    this._emitChange(previous, this._commitPhase(options.emitChange), options.reason ?? 'api');
  }

  protected _convertFromParamate(val: any): any[] {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch { /* not JSON */ }
    }
    return [];
  }

  /** The default comes from a JSON `value` attribute, e.g. value="[0,1,2]". */
  protected override _authoredDefault(): any[] | undefined {
    const attr = this.getAttribute('value');
    return attr === null ? undefined : this._convertFromParamate(attr);
  }

  protected _getInputElement(): null {
    return null;
  }

  public setValue(val: any[], opt?: SetValueOptions): void {
    const { silenceChange, silenceHistory, reason } = resolveSetValueSilence(opt);
    if (silenceHistory) this._skipNextHistoryPush = true;
    this._applyValue(val, {
      emitInput: false,
      emitChange: !silenceChange,
      commit: true,
      reason,
    });
    this._skipNextHistoryPush = false;
  }

  get value(): any[] {
    return [...this._items];
  }

  set value(val: any[]) {
    if (!Array.isArray(val)) return;
    this._warnOnValueSetter();
    this.setValue(val, { silenceChangeEv: true });
  }
}

AsValueList.define();
